import {
  useState, useEffect, useCallback
} from 'react'
import { useMultilingualCaptions } from '../context/LanguageContext'
import { WorkWithParsedSrts } from '../pages/api/work/[id]/index'
import engine from './engine'

// Should cleanup when unmounted?
export function useEngine(work: WorkWithParsedSrts): typeof engine {
  const multilingualCaptions = useMultilingualCaptions(work)

  useEffect(() => {
    // init skips the init process if its been initted already
    engine.init()
      .then(() => {
        if (work) {
          if (multilingualCaptions) engine.loadCaptions(multilingualCaptions)
          engine.load(work)
        }
      })
    // cleanup aka componentWillUnmount
    return () => {
      engine.handleAudioTransport('stop')
    }
  }, [work, multilingualCaptions])

  // useEffect(() => {
  //   engine.loadCaptions(multilingualCaptions)
  // }, [multilingualCaptions])

  return engine
}

export const useWebAudio = () => {
  const [webAudioIsBuffering, setWebAudioIsBuffering] = useState(engine.audioBuffering)
  const [webAudioIsPlaying, setWebAudioIsPlaying] = useState(engine.audioPlaying)
  const [bluetoothLatency, setBluetoothLatency] = useState(engine.bluetoothLatency)
  const [manualLatencyState, setManualLatencyState] = useState(engine.bluetoothLatency >= 65)
  const [outputLatency, setOutputLatency] = useState(0)
  const [sampleRate, setSampleRate] = useState(0)

  const toggleWebAudio = useCallback(() => {
    engine.handleAudioTransport('toggle')
  }, [])

  const startWebAudio = useCallback(() => {
    engine.handleAudioTransport('play')
  }, [])

  const stopWebAudio = useCallback(() => {
    engine.handleAudioTransport('stop')
  }, [])

  const changeBluetoothLatency = useCallback((latency: number) => {
    engine.bluetoothLatency = latency
    setManualLatencyState(latency >= 65)
    setBluetoothLatency(latency)
    if (engine.audioPlaying) {
      engine.handleAudioTransport('play')
    }
  }, [])

  useEffect(() => {
    const audioBufferingCallback = (isBuffering: boolean) => {
      setWebAudioIsBuffering(isBuffering)
    }
    const isPlayingCallback = (isPlaying: boolean) => {
      setWebAudioIsPlaying(isPlaying)
    }
    const reportLatency = (latency: number) => {
      setOutputLatency(latency)
    }
    const reportSR = (sr: number) => {
      setSampleRate(sr)
    }

    engine.ports.webAudio.on('buffering', audioBufferingCallback)
    engine.ports.webAudio.on('playing', isPlayingCallback)
    engine.ports.webAudio.on('output latency', reportLatency)
    engine.ports.webAudio.on('sample rate', reportSR)

    return () => {
      engine.ports.webAudio.off('buffering', audioBufferingCallback)
      engine.ports.webAudio.off('playing', isPlayingCallback)
      engine.ports.webAudio.off('output latency', reportLatency)
      engine.ports.webAudio.off('sample rate', reportSR)
    }
  }, [])

  return {
    webAudioIsBuffering,
    webAudioIsPlaying,
    toggleWebAudio,
    startWebAudio,
    stopWebAudio,
    outputLatency,
    sampleRate,
    bluetoothLatency,
    changeBluetoothLatency,
    manualLatencyState,
  }
}

// export const useHowl = () => {
//   const [howlIsReady, setHowlIsReady] = useState(engine.howl ? engine.howl.playing() : false)
//   const [howlIsPlaying, setHowlIsPlaying] = useState(engine.howl ? engine.howl.playing() : false)
//   const [outputLatency, setOutputLatency] = useState(0)
//   const freeHowl = useCallback(() => {
//     engine.freeHowl()
//   }, [])

//   const toggleHowl = useCallback(() => {
//     engine.setHowlTransport('toggle')
//   }, [])

//   const startHowl = useCallback(() => {
//     engine.setHowlTransport('start')
//   }, [])

//   const stopHowl = useCallback(() => {
//     engine.setHowlTransport('stop')
//   }, [])

//   const loadHowl = useCallback((path) => {
//     engine.loadHowl(path)
//   }, [])

//   useEffect(() => {
//     const isReadyCallback = (isReady: boolean) => {
//       setHowlIsReady(isReady)
//     }
//     const isPlayingCallback = (isPlaying: boolean) => {
//       setHowlIsPlaying(isPlaying)
//     }
//     const reportLatency = (latency: number) => {
//       setOutputLatency(latency)
//     }

//     engine.ports.howl.on('isReady', isReadyCallback)
//     engine.ports.howl.on('isPlaying', isPlayingCallback)
//     engine.ports.howl.on('output latency', reportLatency)

//     return () => {
//       engine.ports.howl.off('isReady', isReadyCallback)
//       engine.ports.howl.off('isPlaying', isPlayingCallback)
//       engine.ports.howl.off('output latency', reportLatency)
//     }
//   }, [])

//   return {
//     howlIsReady,
//     howlIsPlaying,
//     toggleHowl,
//     startHowl,
//     stopHowl,
//     loadHowl,
//     freeHowl,
//     outputLatency,
//   }
// }

export function useSubtitles(): string {
  const [subtitle, setSubtitle] = useState('Waiting for captions...')

  const subtitleCallback = useCallback((s: string) => {
    setSubtitle(s)
  }, [])

  useEffect(() => {
    engine.ports.subtitles.on('subtitle', subtitleCallback)
    return () => {
      engine.ports.subtitles.off('subtitle', subtitleCallback)
    }
  }, [subtitleCallback])
  return subtitle
}

export interface CaptionProgress {
  phase: number;
  milliseconds: number;
  duration: number;
}

// Periodically checks the phase in film.
export function useProgress(updateInterval: number): CaptionProgress {
  const [phase, setPhase] = useState(0)
  const [milliseconds, setMilliseconds] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    const scrubberIntervalID = setInterval(() => {
      setPhase(engine.getPhase())
      setMilliseconds(engine.getLocation())
      setDuration(engine.duration)
    }, updateInterval)
    return () => { clearInterval(scrubberIntervalID) }
  }, [updateInterval])

  return { phase, milliseconds: milliseconds || 0, duration: duration || 0 }
}
